import React, { useState } from 'react'
import Image from 'next/image'
import type { Session } from 'next-auth'
import type { UserResource } from '@clerk/types'
import { useUser } from '@clerk/nextjs'
import { useFeature } from '@growthbook/growthbook-react'

import ExternalLink from 'components/Link/ExternalLink'

import style from './GuestHeaderMenu.module.scss'

import { guestLoginURL } from 'constants/globalEnv'

import CaretIcon from 'assets/icons/icon-caret.svg'

export type GuestHeaderMenuProps = {
  isMobile?: boolean
  children: React.ReactNode
  guestHeaderMenuRef?: React.RefObject<HTMLDivElement>
  session: Session | null
}

const GuestHeaderMenu: React.FC<GuestHeaderMenuProps> = ({
  isMobile,
  children,
  guestHeaderMenuRef,
  session,
}) => {
  const { on: useClerkAuth } = useFeature('clerk-auth')
  // Always call the hook, but only use value when feature flag is on
  const { user: clerkUser } = useUser()

  const [isCollapsed, setIsCollapsed] = useState(false)
  const [showFadeIn, setShowFadeIn] = useState(false)

  // Determine which user data to use based on feature flag
  const hasUser = useClerkAuth ? !!clerkUser : !!session
  const userName = useClerkAuth
    ? clerkUser?.fullName ||
      `${clerkUser?.firstName ?? ''} ${clerkUser?.lastName ?? ''}`
    : session?.user?.name
  const userImage = useClerkAuth ? clerkUser?.imageUrl : session?.user?.image
  const accountUrl = useClerkAuth ? guestLoginURL : session?.sfdc_community_url

  return hasUser ? (
    <div
      className={`${style.menuBody} ${isMobile ? style.isMobile : ''} ${
        isMobile && !isCollapsed ? style.expanded : ''
      } `}
      ref={guestHeaderMenuRef}
    >
      <div
        aria-hidden="true"
        className={`${style.menuMain}`}
        onClick={() => {
          if (isMobile) {
            setShowFadeIn(true)
            setIsCollapsed(!isCollapsed)
          }
        }}
      >
        <div className={`${style.names} ${isMobile ? style.mobile : ''}`}>
          <h5>Guest Account</h5>
          <p>{userName}</p>
        </div>

        {isMobile && userImage && (
          <>
            <div className={style.userAvatarImgContainer}>
              <Image
                alt="Guest Avatar"
                className={style.userAvatarImg}
                height={40}
                src={userImage}
                width={40}
              />
            </div>

            <CaretIcon
              className={`${style.caret} ${!isCollapsed ? style.focused : ''}`}
              title={`Caret ${isCollapsed ? 'Down' : 'Up'}`}
            />
          </>
        )}
      </div>

      <div
        className={`${style.userLinks} ${
          showFadeIn && isMobile && !isCollapsed ? style.fadeIn : ''
        }`}
        data-testid="GuestHeaderMenuExpandablePanel"
      >
        {accountUrl && (
          <ExternalLink
            aria-labelledby="go to guest account"
            href={accountUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            Go to Account
          </ExternalLink>
        )}
        {children}
      </div>
    </div>
  ) : null
}

export default GuestHeaderMenu
